import { Grid, IconButton } from "@mui/material";
import ContentTitle from "../../../../components/Content/ContentTitle";
import { INavigationLink } from "../../../../interfaces/INavigation";
import { useAuthContext } from "../../../../contexts/AuthContext";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import DefaultConstants from "../../../../data/Constants";
import { Subtitles, FilterAlt, Print, Refresh, Search as SearchIcon } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePartnerPmsContext } from "../../../../contexts/PartnerPmsContext";
import Filters from "../../../../components/PartnerPms/Filters";
import RoomCard from "../../../../components/PartnerPms/RoomCard";
import Legend from "../../../../components/PartnerPms/Legend";
import GovernanceDialog from "../../../../components/PartnerPms/Governance/GovernanceDialog";
import { IRoom } from "../../../../interfaces/IPartnerPms";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import Search from "../../../../components/Search";
import { useReportPreviewContext } from "../../../../contexts/ReportPreviewContext";
import { GovernanceReports, ReportsAlias } from "../../../../data/Reports";
import IReport from "../../../../interfaces/IReport";
import Tooltip from "../../../../components/Tooltip";
import { IRoomOverviewReportFilters } from "../Reports/RoomOverview/Filters";
import Empty from "../../../../components/Empty";
import useDrawer from "../../../../hooks/useDrawer";
import useSearch from "../../../../hooks/useSearch";
import StringHelper from "../../../../helpers/StringHelper";
import { GovernanceStatusData } from "../../../../data/Enums";
import useState from "../../../../hooks/useState";
import SortBy from "../../../../components/PartnerPms/SortBy";


interface IStateValues {
    roomsOnScreen: number;
    selectedRoom: IRoom | undefined;
}

const RoomOverview = (linkProps: INavigationLink) => {
    const { environment } = useEnvironmentContext();
    const { translate } = useLanguageContext();
    const { enterprise, hasPermission } = useAuthContext();
    const { 
        filteredRooms,
        handleRefreshRooms,
        selectedBlock,
        selectedFloor,
        selectedGovernanceStatus,
        selectedOccupancyStatus,
        selectedRoomStatus,
        selectedRoomType, 
        onlyUserRooms,
        sortBy
    } = usePartnerPmsContext();
    const { open: reportPreviewOpen,  handleOpen } = useReportPreviewContext();

    const containerRef = useRef<HTMLDivElement | null>(null);

    const legendDrawer = useDrawer();
    const filterDrawer = useDrawer();
    
    const search = useSearch();
    
    const state = useState<IStateValues>({
        roomsOnScreen: 100,
        selectedRoom: undefined
    });
    

    const handleRefresh = useCallback((showLoading: boolean | undefined = undefined) => {
        const conditionsToShowLoading = [
            !reportPreviewOpen,
            state.values.selectedRoom === undefined,
            !legendDrawer.open, 
            !filterDrawer.open, 
            !search.open
        ];

        showLoading = showLoading !== undefined ? showLoading : conditionsToShowLoading.every(c => c === true);
        handleRefreshRooms(() => {}, showLoading);
        // eslint-disable-next-line
    }, [state.values.selectedRoom, legendDrawer.open, filterDrawer.open, search.open, reportPreviewOpen]);


    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        if (state.values.selectedRoom !== undefined) {
            return;
        }
            
        const milliseconds = environment.partnerPms.dataRefreshTimeInSeconds * 1000;
        const interval = setInterval(() => {
            handleRefresh();
        }, milliseconds);
            
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [handleRefresh, state.values.selectedRoom]);


    useEffect(() => {
        if (containerRef.current) {
            const container = containerRef.current;
            const roomsOnScreen = state.values.roomsOnScreen;
                
            container.onscroll = () => {
                const onContainerBottom = Math.abs(container.scrollHeight - container.clientHeight - container.scrollTop) < 10;
                if (onContainerBottom && roomsOnScreen < filteredRooms.length) {
                    state.update('roomsOnScreen', roomsOnScreen + 100);
                }
            }
        }

        // eslint-disable-next-line
    }, [filteredRooms, state.values.roomsOnScreen, containerRef]);



    const handleOpenReportPreview = useCallback(() => {
        const report = GovernanceReports.find(r => r.alias === ReportsAlias.Governance.RoomOverview) as IReport;
        const filters: IRoomOverviewReportFilters = {
            enterprise: enterprise,
            occupancyStatus: selectedOccupancyStatus,
            governanceStatus: selectedGovernanceStatus,
            roomStatus: selectedRoomStatus,
            block: selectedBlock,
            floor: selectedFloor,
            roomType: selectedRoomType,
            onlyUserRooms: onlyUserRooms,
            sortBy: sortBy
        };

        handleOpen(report, filters);
        // eslint-disable-next-line
    }, [enterprise, selectedOccupancyStatus, selectedGovernanceStatus, selectedRoomStatus, selectedBlock, selectedFloor, selectedRoomType, onlyUserRooms, sortBy]);


    const roomCards = useMemo(() => {
        const allowedGovernanceStatus = Object.values(GovernanceStatusData)
            .filter(s => hasPermission(`Governance.RoomOverview.View.${StringHelper.FirstLetterUpperCase(s.name)}`))
            .map(s => s.partnerId);

        let roomsAux = filteredRooms.filter(r => allowedGovernanceStatus.includes(r.governanceStatusId));

        if (search.value.trim().length > 0) {
            const value = StringHelper.Normalize(search.value.toUpperCase());
            roomsAux = roomsAux.filter(r => StringHelper.Normalize(r.description.toUpperCase()).includes(value));
        }
        
        return roomsAux.slice(0, state.values.roomsOnScreen).map(r => {
            return <Grid item lg={1.2} md={2.4} sm={4} xs={6} key={r.id}>
                <RoomCard
                    room={r}
                    showGovernanceTooltip={true}
                    key={r.id}
                    onClick={() => state.update('selectedRoom', r)}
                />
            </Grid>;
        });
        // eslint-disable-next-line
    }, [filteredRooms, state.values, search.value, sortBy]);

    
    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle 
                        name={translate(linkProps.name)} 
                        description={enterprise?.name}
                        applyTranslation={false}
                    />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        hasPermission(ReportsAlias.Governance.RoomOverview) && (
                            <Tooltip title={translate('print')}>
                                <IconButton onClick={handleOpenReportPreview}>
                                    <Print />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    <Tooltip title={translate('search')}>
                        <IconButton onClick={() => search.handleOpen()}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('legend')}>
                        <IconButton onClick={() => legendDrawer.handleOpen()}>
                            <Subtitles />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filterDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={() => handleRefresh(true)}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            {
                roomCards.length > 0 ? (
                    <Grid 
                        container 
                        className='grid-content overflow-enabled justifycontent-flexstart aligncontent-flexstart' 
                        rowSpacing={DefaultConstants.gridRowSpacing} 
                        columnSpacing={DefaultConstants.gridColumnSpacing}
                        ref={containerRef}
                    >
                        <Grid item xs={12} marginTop={-1} marginBottom={2}>
                            <SortBy />
                        </Grid>

                        {roomCards}
                    </Grid>
                ) : (
                    <Grid container className='grid-content'>
                        <Grid item xs={12} className='paper-content display-flex alignitems-center justifycontent-center background-transparent boxshadow-none'>
                            <Empty text='noRoomsFound' className="backgroundcolor-paper boxshadow-default" />
                        </Grid>
                    </Grid>
                )
            }

            <Search
                open={search.open}
                handleClose={() => search.handleClose()}
                value={search.value}
                handleChange={(e) => search.handleChangeValue(e.currentTarget.value)}
            />

            <Legend
                open={legendDrawer.open}
                handleClose={() => legendDrawer.handleClose()}
            />

            <Filters
                open={filterDrawer.open}
                handleClose={() => filterDrawer.handleClose()}
            />

            <GovernanceDialog
                open={state.values.selectedRoom !== undefined}
                handleClose={() => {
                    state.update('selectedRoom', undefined);
                    handleRefresh(true);
                }}
                roomId={state.values.selectedRoom?.id}
            />
        </>
    );
}

export default RoomOverview;